import Vue from 'vue';
import store from 'store';
import { mapActions } from 'vuex';
import { i18n } from 'lib/i18n.js';
import { htmlDecode, decode, formatPrice, isTablet } from 'utils/general';
import bus from 'lib/bus';
import productBundle from 'mixins/product-bundle';
import {
  listProductsEventData,
  trackEvent,
  getLocalStorageProperties,
} from '../../assets/js/lib/tracking';
import { getAllValueForTag } from '../../assets/js/utils/general';

export default {
  i18n,
  components: {
    'vue-image': () => import('../vue-image/vue-image.vue'),
    'vue-image-v2': () => import('../vue-image-v2/vue-image-v2.vue'),
  },
  data() {
    return {
      isTablet: isTablet(),
      barCollapsed: false,
      date: Date.now(),
    };
  },
  mixins: [productBundle],
  props: [
    'bundleSize',
    'selectedItems',
    'deletingIndex',
    'discount',
    'bundleProduct',
  ],
  created() {
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  watch: {
    async bundleSet(_new, _old) {
      if (this.bundleSet) {
        const { products } = await listProductsEventData(this.selectedItems, {
          origin: document.body.dataset.origin,
          originProduct: document.body.dataset.originProduct,
          isBundleBuilder: true,
          bundleName: htmlDecode(this.bundleProduct.title),
          bundleId: String(this.bundleProduct.id),
          listName: this.setText,
          listId: 'bundle-builder-bar',
        });

        trackEvent({
          event: 'view_item_list',
          site_section: window.__GLOBAL__.siteSection,
          component_name: 'Bundle Builder Your Set',
          bundle_id: String(this.bundleProduct.id),
          bundle_name: htmlDecode(this.bundleProduct.title),
          bundle_type: 'bundle builder',
          item_list_name: this.setText,
          ecommerce: {
            currency: window.Shopify.currency.active,
            value: this.discountedPriceValue / 100,
            items: products,
          },
        });
      }
    },
  },
  computed: {
    setValue() {
      return this.selectedItems.reduce((summ, item) => {
        let priceSource = item;
        if (item.activeVariant) {
          priceSource = item.activeVariant;
        }
        if (item.hasSizes) {
          priceSource = item.variants.find((variant) =>
            variant.bundleBuilderProductIds?.includes(this.bundleProduct.id),
          ) || item.variants[0];
        }
        summ += Number(priceSource.price);
        return summ;
      }, 0);
    },
    totalPrice() {
      if (this.selectedItems.length === 0 || this.discount === 0) return false;
      return formatPrice(this.setValue);
    },
    discountedPrice() {
      if (this.selectedItems.length === 0) return formatPrice(this.setValue);
      return formatPrice(this.setValue - this.setValue * (this.discount / 100));
    },
    discountedPriceValue() {
      if (this.selectedItems.length === 0) return this.setValue;
      return this.selectedItems.length < 2
        ? this.setValue * (this.discount / 100)
        : this.setValue - this.setValue * (this.discount / 100);
    },
    discountText() {
      if (this.selectedItems.length === 0 || this.discount === 0) return false;
      return this.$t('bundle_builder.percentage_off').replace(
        '{percentage}',
        this.discount,
      );
    },
    cartTotalPrice() {
      if (this.selectedItems.length === 0 || this.discount === 0) return false;
      return this.setValue;
    },
    cartDiscountedPrice() {
      if (this.selectedItems.length === 0) return this.setValue;

      return this.selectedItems.length < 2
        ? this.setValue * (this.discount / 100)
        : this.setValue - this.setValue * (this.discount / 100);
    },
    setText() {
      return this.$t('bundle_builder.your_set');
    },
    buttonText() {
      if (this.bundleSet) {
        return this.$t('bundle_builder.add_bundle_to_bag');
      }
      return `${this.selectedItems.length}/${this.bundleSize} selected`;
    },
    bundleSet() {
      return this.selectedItems.length === this.bundleSize;
    },
  },
  methods: {
    ...mapActions('cart', [
      'addBundleItemsToCart',
      'closeCart',
      'updateChangePending',
    ]),
    onResize() {
      this.isTablet = isTablet();
    },
    getProductTitle(title) {
      return htmlDecode(title.split(window.__GLOBAL__.productDelimiter)[0]);
    },
    getVariantTitle(item) {
      if (item.activeVariant) {
        return htmlDecode(item.activeVariant.title);
      }
      if (item.hasSizes) {
        const sizeVariant = item.variants.find((variant) =>
          variant.bundleBuilderProductIds?.includes(this.bundleProduct.id),
        );
        if (sizeVariant) {
          const splitTitle = item.title.split(window.__GLOBAL__.productDelimiter);
          const decodedTitle = splitTitle[1] ? `${htmlDecode(splitTitle[1])} / ` : '';
          const option = sizeVariant.option2 !== false && sizeVariant.option2 !== 'false' 
          ? sizeVariant.option2 
          : sizeVariant.option1;
          return (decodedTitle + option);
        }
      }
      return item.title.split(window.__GLOBAL__.productDelimiter)[1];
    },
    removeProductFromBundle(item) {
      bus.$emit('bb-item-added', item);
    },
    collapseBar() {
      this.barCollapsed = true;
    },
    getBundleIdFromProduct(product) {
      if (product.activeVariant) {
        return product.activeVariant.id;
      }

      if (product.hasSizes) {
        const sizeVariant = product.variants.find((variant) =>
          variant.bundleBuilderProductIds?.includes(this.bundleProduct.id),
        );
        if (sizeVariant) {
          return sizeVariant.id;
        }
      }
      
      return product.variants[0].id;
    },
    getBundleUid(productObject, bundle) {
      return bundle.reduce((uid, product) => {
        const activeVariant = this.getBundleIdFromProduct(product);
        return `${uid}_${activeVariant}`;
      }, `${productObject.id}`);
    },
    getBundleSKU(bundle) {
      return bundle.reduce((uid, product) => {
        const activeVariant = product.activeVariant
          ? product.activeVariant
          : product.variants[0];
        return `${uid}${activeVariant.sku},`;
      }, '');
    },
    addBundle() {
      const callback = () => {
        setTimeout(() => {
          this.closeCart();
          this.updateChangePending(false);
          bus.$emit('bb-clear-items');
        }, 2500);
      };
      this.collapseBar();
      const bundleUid = this.getBundleUid(
        this.bundleProduct,
        this.selectedItems,
      );
      const bProduct = {
        ...this.bundleProduct,
      };
      bProduct.price = this.discountedPriceValue;
      this.date = new Date().getTime();
      this.addBundleItemsToCart({
        lines: this.selectedItems.reduce(
          ({ lines, index }, product) => {
            const productTags = getAllValueForTag('badge', product.tags);
            const encodedTags =
              Array.isArray(productTags) && productTags.length
                ? productTags.map((tag) => encodeURIComponent(tag))
                : encodeURIComponent(productTags);

            lines.push({
              id: this.getBundleIdFromProduct(product),
              quantity: 1,
              properties: {
                _bundle_builder: true,
                _bundle: this.bundleProduct.id,
                _bundle_uid: bundleUid,
                _bundle_cart_uid: `${bundleUid}-${this.date}`,
                _bundle_index: index,
                _bundle_price: parseInt(this.cartDiscountedPrice, 10),
                _bundle_title: htmlDecode(this.bundleProduct.title),
                _bundle_image: this.bundleProduct.featuredImage,
                _bundle_compare_price: parseInt(this.cartTotalPrice, 10),
                _bundle_product_title: htmlDecode(product.title),
                _bundle_product_price: product.price,
                _bundle_discount_value: parseInt(this.discount, 10),
                _product_rating:
                  product.reviewsAverage ||
                  product.metafields?.yotpo?.reviews_average,
                _bundle_validated: true,
                _origin: 'PDP',
                _origin_product: htmlDecode(this.bundleProduct.title),
                _index: product.index + 1,
                _item_list_id: product.listId,
                _item_list_name: product.listName,
                _product_type: product.product_type,
                _bundle_type: 'bundle builder',
                _product_context: encodedTags,
                _bundle_product_url: this.bundleProduct.url,
              },
            });
            index++;
            return { lines, index };
          },
          { lines: [], index: 0 },
        ).lines,
        quantity: 1,
        bundleProduct: bProduct, // price for event
        bundles: this.selectedItems,
        otherData: {
          ...getLocalStorageProperties(),
          componentName: 'Bundle Builder',
          siteSection: window.__GLOBAL__.siteSection,
          bundleType: 'bundle builder',
          multiple: false,
          discount: this.discount,
        },
        callback,
      });
    },
  },
};
